import React, { useEffect, useState } from 'react';

import { useAuth } from '../../systems/auth-context';

import { CharacterPortrait } from '../../types';

import { AssetField } from './AssetPickerModal';

import { EmotionsEditor } from './EmotionsEditor';

import { ALLOWED_EMOTIONS, AllowedEmotion, normalizeEmotionsRecord } from './emotion-presets';

import {

  buildCharacterFromImport,

  mergeCharacterFromZipUpdate,

  parseCharacterZip,

} from './character-zip-import';

import { assetUrl, uploadAsset } from './scenario-api';

import { uniqueId } from './utils';

import styles from './scenario-studio.module.css';



interface CharacterEditorProps {

  scenarioId: string;

  characters: CharacterPortrait[];

  onChange: (characters: CharacterPortrait[]) => void;

}



export const CharacterEditor: React.FC<CharacterEditorProps> = ({

  scenarioId,

  characters,

  onChange,

}) => {

  const { user } = useAuth();

  const [importing, setImporting] = useState(false);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const validIds = new Set(characters.map((c) => c.id));
    setExpandedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [characters]);

  const isExpanded = (id: string) => expandedIds.has(id);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ensureExpanded = (id: string) => {
    setExpandedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };



  const addCharacter = () => {

    const id = uniqueId('char');

    onChange([

      ...characters,

      {

        id,

        name: 'Nouveau personnage',

        defaultImage: '',

        emotions: { neutre: '' },

      },

    ]);

  };



  const uploadZipEntries = async (
    characterId: string,
    entries: Awaited<ReturnType<typeof parseCharacterZip>>['entries']
  ): Promise<Map<AllowedEmotion, string>> => {
    const uploadedPaths = new Map<AllowedEmotion, string>();

    for (const entry of entries) {
      const uploadFile = new File([entry.blob], entry.filename, {
        type: entry.blob.type || 'image/png',
      });
      const relPath = `${characterId}/${entry.filename}`;
      const assetPath = await uploadAsset(scenarioId, uploadFile, 'characters', relPath);
      uploadedPaths.set(entry.emotion, assetPath);
    }

    return uploadedPaths;
  };

  const handleImportZip = async (e: React.ChangeEvent<HTMLInputElement>) => {

    const file = e.target.files?.[0];

    e.target.value = '';

    if (!file || !user) return;



    setImporting(true);

    try {

      const parsed = await parseCharacterZip(file);

      const uploadedPaths = await uploadZipEntries(parsed.characterId, parsed.entries);



      const imported = buildCharacterFromImport(parsed, uploadedPaths);

      const existingIds = new Set(characters.map((c) => c.id));

      if (existingIds.has(imported.id)) {

        imported.id = uniqueId(imported.id);

      }



      ensureExpanded(imported.id);
      onChange([...characters, imported]);

    } catch (err) {

      alert(err instanceof Error ? err.message : 'Import ZIP échoué');

    } finally {

      setImporting(false);

    }

  };



  const handleUpdateZip = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {

    const file = e.target.files?.[0];

    e.target.value = '';

    if (!file || !user) return;



    const char = characters[index];

    setUpdatingId(char.id);

    try {

      const parsed = await parseCharacterZip(file);

      const uploadedPaths = await uploadZipEntries(char.id, parsed.entries);

      const patch = mergeCharacterFromZipUpdate(char, uploadedPaths);

      update(index, patch);

    } catch (err) {

      alert(err instanceof Error ? err.message : 'Mise à jour ZIP échouée');

    } finally {

      setUpdatingId(null);

    }

  };



  const update = (index: number, patch: Partial<CharacterPortrait>) => {

    const merged = { ...characters[index], ...patch };

    if (patch.emotions) {
      // patch.emotions is the full editor state (EmotionsEditor replaces the record, including deletions).
      merged.emotions = normalizeEmotionsRecord(patch.emotions);
    }

    onChange(characters.map((c, i) => (i === index ? merged : c)));

  };



  const updateDefaultImage = (index: number, defaultImage: string) => {

    const char = characters[index];

    const emotions = { ...(char.emotions ?? {}) };

    if (defaultImage && (emotions.neutre === undefined || emotions.neutre === '')) {

      emotions.neutre = defaultImage;

    }

    update(index, { defaultImage, emotions });

  };



  const remove = (index: number) => {

    onChange(characters.filter((_, i) => i !== index));

  };



  return (

    <div className={styles.panel}>

      <div className={styles.panelHeader}>

        <h3>Personnages</h3>

        <div className={styles.row}>

          <label

            className={`${styles.btnSecondary} ${importing ? styles.btnLoading : ''}`}

            title="Importer un dossier asset_char_* zippé (sprites emotion_V1)"

          >

            {importing ? (

              <span className={styles.loaderInline} style={{ padding: 0 }}>

                <span className={styles.spinner} /> Import…

              </span>

            ) : (

              'Import ZIP'

            )}

            <input

              type="file"

              accept=".zip,application/zip"

              hidden

              disabled={importing}

              onChange={handleImportZip}

            />

          </label>

          <button type="button" className={styles.btnSecondary} onClick={addCharacter} disabled={importing}>

            + Ajouter

          </button>

        </div>

      </div>

      {characters.map((char, index) => {
        const expanded = isExpanded(char.id);
        const emotionCount = Object.values(char.emotions ?? {}).filter(Boolean).length;
        const updating = updatingId === char.id;

        return (
          <div key={char.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <button
                type="button"
                className={styles.expandToggle}
                onClick={() => toggleExpanded(char.id)}
                aria-expanded={expanded}
                title={expanded ? 'Réduire' : 'Développer'}
              >
                {expanded ? '▾' : '▸'}
              </button>

              {char.defaultImage && (
                <img
                  className={styles.cardThumb}
                  src={assetUrl(scenarioId, char.defaultImage)}
                  alt=""
                />
              )}

              <div className={styles.cardHeaderMain}>
                <input
                  className={styles.input}
                  value={char.name}
                  placeholder="Nom affiché"
                  onChange={(e) => update(index, { name: e.target.value })}
                />
                {!expanded && (
                  <span className={styles.cardMeta}>
                    {char.id}
                    {emotionCount > 0 && ` · ${emotionCount} émotion${emotionCount > 1 ? 's' : ''}`}
                  </span>
                )}
              </div>

              <label
                className={`${styles.btnSecondary} ${updating ? styles.btnLoading : ''}`}
                title="Mettre à jour les sprites depuis un zip DarwinX (remplace les émotions existantes, ajoute les manquantes)"
              >
                {updating ? (
                  <span className={styles.loaderInline} style={{ padding: 0 }}>
                    <span className={styles.spinner} /> Update…
                  </span>
                ) : (
                  'Update ZIP'
                )}
                <input
                  type="file"
                  accept=".zip,application/zip"
                  hidden
                  disabled={updating || importing}
                  onChange={(e) => handleUpdateZip(index, e)}
                />
              </label>

              <button type="button" className={styles.btnDanger} onClick={() => remove(index)}>
                Supprimer
              </button>
            </div>

            {expanded && (
              <div className={styles.cardBody}>
                <div className={styles.field}>
                  <label>ID</label>
                  <input
                    className={styles.input}
                    value={char.id}
                    onChange={(e) => update(index, { id: e.target.value })}
                  />
                </div>

                <AssetField
                  scenarioId={scenarioId}
                  label="Portrait par défaut"
                  value={char.defaultImage}
                  subdir="characters"
                  onChange={(defaultImage) => updateDefaultImage(index, defaultImage)}
                />

                <AssetField
                  scenarioId={scenarioId}
                  label="Icône dialogue"
                  value={char.icon ?? ''}
                  subdir="characters"
                  onChange={(icon) => update(index, { icon })}
                />

                <EmotionsEditor
                  scenarioId={scenarioId}
                  emotions={char.emotions ?? {}}
                  onChange={(emotions) => update(index, { emotions })}
                />
              </div>
            )}
          </div>
        );
      })}

    </div>

  );

};


